import type { QUALITY } from "../render/quality.ts";
import type { WebGLRenderer } from "three";
import { classOf, ITEM_LOOKS, type ClassId, type Mode } from "@eldrun/content";
import { ENTRY_REFUSALS, MSG, type DamageKind, type DamageProcs, type RematchEvent } from "@eldrun/protocol";
import { createApi } from "@eldrun/api-client";
import type { ClientConfig } from "./config.ts";
import { Account, browserStore } from "../net/account.ts";
import { MatchClient, type MatchSession } from "../net/match-client.ts";
import { endView } from "../ui/screens/end-model.ts";
import { h, mount } from "../ui/kit/dom.ts";
import { showLoading, type LoadingScreen } from "../ui/screens/loading.ts";
import { renderLobby } from "../ui/screens/lobby.ts";
import { leaveMessage } from "../ui/screens/lobby-model.ts";
import { ClassSelect } from "../ui/screens/class-select.ts";
import { chooseClassTheme, rememberedClass } from "../ui/theme/class-theme.ts";
import { startClassStage, type ClassStage, type ClassStageProbe } from "../scenes/class-stage.ts";
import { showMenu, type MenuChoice } from "../ui/screens/menu.ts";
import { audio } from "../audio/engine.ts";
import type { AudioStats } from "../audio/engine.ts";
import { ItemIcons } from "../render/item-icons.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { models, type ModelStats } from "../render/model-library.ts";
import type { ShadingProbeOptions, ShadingProbeResult } from "../render/shading-probe.ts";
import type { BlobTell, SkinningProbe } from "../scenes/creature-viewer.ts";
import { toast } from "../ui/hud/toast.ts";
import { loadWorld, type LoadedWorld } from "../world/load-world.ts";
import type { CreatureSnapshot } from "../game/creature-layer.ts";
import type { PerfSummary } from "../engine/frame-loop.ts";
import { avatars, type AvatarLoadStats } from "../render/avatar-library.ts";
import { GameScene, type AvatarProbe } from "../scenes/game-scene.ts";

export type Screen = "menu" | "classe" | "conectando" | "lobby" | "carregando" | "jogando" | "fim" | "ilha" | "criaturas" | "itens" | "estruturas" | "entulho" | "chamas" | "sons" | "avatares" | "neon" | "ui";

/** Estado exposto em `window.__eldrun` para testes de ponta a ponta. */
export interface AppProbe {
  screen: Screen;
  ready: boolean;
  seed?: number;
  mode?: Mode;
  genVersion: number;
  roomCode?: string;
  sessionId?: string;
  phase?: string;
  worldHash?: number | undefined;
  serverWorldHash?: number | undefined;
  loadMs?: number;
  error?: string;
  stats?: () => Record<string, unknown>;
  /** Visor dos avatares (F120): desenha um quadro e devolve o canvas em PNG (com alfa no fundo transparente). */
  snapshot?: () => string;
  /** Ícones dos itens com modelo desenhados agora no carregamento (F47; 0 se o cache da página já os tinha). */
  prewarmedIcons?: number;
  /** Posições dos corpos como este cliente as vê (fase "jogando"). */
  avatars?: () => AvatarProbe[];
  /** Criaturas como este cliente as vê (posição interpolada), na fase "jogando". */
  creatures?: () => CreatureSnapshot[];
  /** Ação principal sem pointer lock (só em desenvolvimento, para os e2e): clique, ou aperta/solta. */
  primary?: (held?: boolean) => void;
  /** Mira sem pointer lock (só em desenvolvimento, para os e2e). */
  look?: (yaw: number, pitch: number) => void;
  /** Põe a cruz da terceira pessoa sobre um ponto do mundo (só em desenvolvimento, para os e2e; F55). */
  aimAt?: (x: number, y: number, z: number) => { yaw: number; pitch: number };
  /** Roda `n` quadros da câmera (rig, mira e tremor) e devolve os ms (só em desenvolvimento; F55, `budgets`). */
  cameraBench?: (n: number) => number;
  /** O ponto de interesse do tipo mais perto (só em desenvolvimento; F55, capturas da câmera). */
  testPoi?: (type: string) => { x: number; z: number; rot: number } | undefined;
  /** Dano no próprio corpo (só em desenvolvimento, servidor com `ELDRUN_TEST_HOOKS=1`). */
  selfDamage?: (amount: number) => void;
  /** Um chefe na frente com a vida dada (só em desenvolvimento, servidor com `ELDRUN_TEST_HOOKS=1`). */
  testBoss?: (type: string, hp: number) => void;
  /** Mandinga, redemoinho perto ou travessura (só em desenvolvimento, servidor com `ELDRUN_TEST_HOOKS=1`). */
  testSaci?: (options: { mandinga?: string; whirlwind?: number; prank?: string }) => void;
  /** Jangada pronta, vida do Boitatá ou derrubá-lo (só em desenvolvimento, servidor com `ELDRUN_TEST_HOOKS=1`). */
  testRaft?: (options: { complete?: boolean; boitataHp?: number; killBoitata?: boolean }) => void;
  /** Força a entrada dos leitos de ambiente, só no cliente (só em desenvolvimento; F46); `null` volta ao jogo. */
  testAmbience?: (options: { coast?: number; land?: boolean; biome?: string; night?: number; raftStage?: string; underwater?: boolean } | null) => void;
  /** O Cerco fechado neste raio em volta deste jogador (só em desenvolvimento, servidor com `ELDRUN_TEST_HOOKS=1`). */
  testCerco?: (radius: number) => void;
  /** `n` assombrações sem dano em volta deste jogador (só em desenvolvimento, servidor com `ELDRUN_TEST_HOOKS=1`; F36). */
  testCreatures?: (n: number) => void;
  /** Danos fictícios em volta deste jogador, só no cliente (números, impacto, passivas); só em desenvolvimento (Fase 12 Gate C). */
  testHits?: (list: readonly { dx: number; dz: number; fromMe: boolean; amount?: number; kind?: DamageKind; procs?: DamageProcs }[]) => void;
  /** Um golpe deste jogador num alvo fictício à frente, com o dano no instante do impacto; só visual, só em desenvolvimento (Fase 14 G0). */
  testStrike?: (options?: { ahead?: number; side?: number; crit?: boolean; died?: boolean; amount?: number; cycle?: number }) => void;
  /** Imagens (JPEG, data URL) dos quadros desenhados depois de cada atraso em ms; só em desenvolvimento (Fase 14 G0). */
  testGrab?: (delays: readonly number[]) => Promise<string[]>;
  /** Geometrias, texturas e programas vivos no renderer, em qualquer tela (Fase 12 Gate B1). */
  gpuMemory?: () => { geometries: number; textures: number; programs: number };
  /** Efeitos de habilidades em volta deste jogador, cada um na cor pedida; só visual, só em desenvolvimento (Fase 12 Gate A). */
  testSkills?: (list: readonly { skill: string; color: string; dx: number; dz: number; radius: number; ms: number; dx2?: number; dz2?: number; own?: boolean }[]) => void;
  /** Esconde grupos de efeito (`peca-de-habilidade`, `estilhacos`, `fumaca`, `energia`) para medir a GPU de cada um; só em desenvolvimento (Fase 12 Gate D2). */
  testHideFx?: (groups: readonly string[]) => void;
  /** Leva este jogador ao chão livre em (x, z) ou mais perto dele (só em desenvolvimento, servidor com `ELDRUN_TEST_HOOKS=1`). */
  testGoto?: (x: number, z: number) => void;
  /** Pixels de capim vistos de cima num quadrado de `half` m em volta de (x, z); `raw`, sem as construções (só em desenvolvimento; F53). */
  grassBlades?: (x: number, z: number, half: number, raw?: boolean) => number;
  /** Sonda de sombreamento das vitrines (F51): 40 pixels RGBA no dia, no crepúsculo e na noite (F52: `post`, pelo pós de um tier). */
  shadingProbe?: (options?: ShadingProbeOptions) => ShadingProbeResult;
  /** Manequim com skinning da vitrine das criaturas (F51). */
  skinning?: () => SkinningProbe;
  /** Chão ao lado das criaturas com e sem as sombras-blob, na vitrine (F51). */
  blobTell?: (ids: readonly string[]) => BlobTell[];
  /** Chão ao lado dos bonecos da ilha com e sem as sombras-blob (F51); `grass: false`, só o terreno. */
  islandBlobTell?: (options?: { grass?: boolean }) => { samples: { on: number[] | null; off: number[] | null }[] }[];
  /** Fase 13 (só nas vitrines): GPU mediana (ms) do desenho do quadro em `frames` quadros; `undefined` sem o medidor. */
  gpuProfile?: (frames: number) => Promise<number | undefined>;
  /** Fase 13 (só nas vitrines): troca opções do pós e das sombras em uso (para medir cada efeito). */
  setGraphics?: (patch: Partial<typeof QUALITY.post> & { shadows?: boolean }) => void;
  /** Só o capim com uma sombra-blob de teste num lugar com capim perto da câmera, com e sem ela (F51). */
  grassTell?: () => { x: number; z: number; covered: number; on: number[]; off: number[] } | undefined;
  /** Draw calls do próximo render por camada (diagnóstico de desempenho, F36). */
  drawBreakdown?: () => Promise<Record<string, number>>;
  /** Orçamentos medidos desde o último `perf(true)`: quadros, JS (média e pior), draw calls e triângulos máximos (F36). */
  perf?: (reset?: boolean) => PerfSummary;
  audio?: () => AudioStats;
  models?: () => ModelStats;
  /** Carregamento dos avatares animados (F49; F88: o esqueleto e os personagens pedidos). */
  avatarLoad?: () => AvatarLoadStats;
  /** O palco 3D da escolha da classe (F85), enquanto aberto: os modelos, o destaque e a escolha. */
  classStage?: () => ClassStageProbe | undefined;
}

/** O "Jogar sozinho" mostra a escolha (o golpe e o som da classe) antes de abrir a partida, em ms. */
const SOLO_CONFIRM_MS = 600;

const END_REASONS: Record<string, string> = {
  abandono: "A partida acabou: ficaram menos jogadores conectados que o mínimo do modo.",
  carregamento: "A partida não começou: menos jogadores que o mínimo do modo carregaram a ilha a tempo.",
  "erro-servidor": "O servidor teve um problema ao preparar a ilha.",
  derrota: "Derrota: o grupo inteiro caiu ao mesmo tempo.",
  vitoria: "Vitória! O Dragão caiu e o Barco segue viagem.",
  treta: "Fim da Arena: o último em pé leva a partida.",
};

/** Derrota de quem estava sozinho na sala (F54): com a volta paga oferecida (e recusada) ou sem ela. */
const SOLO_DEFEAT = "Derrota: você caiu e não pagou a volta na Estátua da Fênix.";
const ALONE_DEFEAT = "Derrota: você caiu sem ninguém para te reviver.";

/** Texto do motivo do fim; a derrota de quem estava sozinho tem o próprio (F54). */
function endReasonText(reason: string, state: MatchSession["state"] | undefined, me: string | undefined): string | undefined {
  if (reason !== "derrota" || !state || [...state.players.values()].filter((p) => p.connected).length > 1) return END_REASONS[reason];
  // A oferta fica marcada no estado de quem morreu (o servidor para de atualizar ao fim).
  return me && state.deaths?.get(me)?.solo ? SOLO_DEFEAT : ALONE_DEFEAT;
}

/**
 * Fluxo do app: menu → conexão → lobby → carregamento da ilha (confirmando o hash com o
 * servidor) → partida → fim. Cada tela só reflete o estado que o servidor manda.
 */
export class AppFlow {
  private readonly renderer: WebGLRenderer;
  private readonly ui: HTMLElement;
  private readonly client: MatchClient;
  private readonly probe: AppProbe;
  private session: MatchSession | undefined;
  private world: LoadedWorld | undefined;
  private loading: LoadingScreen | undefined;
  private loadingStarted = false;
  /** Cancela a geração em andamento ao sair da sala ou quando a partida acaba. */
  private loadAbort: AbortController | undefined;
  private reported = false;
  /** Sons pré-renderizados (a confirmação ao servidor espera por eles). */
  private assetsReady = false;
  private game: GameScene | undefined;
  /** Trocando de sala (revanche): sair da antiga não volta ao menu. */
  private switching = false;
  /** Conta (F34): login no menu, token na entrada, conquistas no fim. */
  private readonly account: Account;
  private readonly siteUrl: string;
  /** Para a consulta das conquistas ao sair da tela de fim. */
  private achievementsAbort: AbortController | undefined;
  /** Conta com que entrou na sala (vazio: convidado): a revanche só segue com a mesma. */
  private entryAccount = "";
  /** Aviso para o menu quando a saída da sala foi nossa (revanche com a conta trocada). */
  private leaveNotice: string | undefined;
  /** Pós-processamento (F52): aquecido no carregamento e entregue à cena de jogo, que o solta ao sair. */
  private readonly post: PostFx;
  /** A tela de escolha da classe (F85) aberta, com o palco 3D quando ele fica pronto. */
  private picker: { mode: "solo" | "lobby"; ui: ClassSelect; stage: ClassStage | undefined; abort: AbortController; chosen: ClassId | undefined; n: number } | undefined;

  constructor(renderer: WebGLRenderer, ui: HTMLElement, config: ClientConfig, probe: AppProbe) {
    this.renderer = renderer;
    this.ui = ui;
    this.client = new MatchClient(config.gameServerUrl);
    this.account = new Account(createApi(config.apiUrl), browserStore());
    this.siteUrl = config.siteUrl;
    this.probe = probe;
    this.post = new PostFx(renderer);
    // Recursos de GPU vivos em qualquer tela (Gate B1): a reentrada na partida não pode fazê-los crescer.
    probe.gpuMemory = () => ({ ...renderer.info.memory, programs: renderer.info.programs?.length ?? 0 });
  }

  showMenu(message?: string): void {
    this.closePicker();
    this.setScreen("menu");
    // "Jogar sozinho" passa pela escolha da classe (F85); as salas, pelo lobby (que tem a mesma escolha).
    showMenu(this.ui, (choice) => (choice.action === "sozinho" ? this.pickSolo(choice) : void this.join(choice)), message, { account: this.account, siteUrl: this.siteUrl });
  }

  /** A escolha 3D da classe antes do "Jogar sozinho" (F85): escolher já entra; voltar, ao menu. */
  private pickSolo(choice: MenuChoice): void {
    this.setScreen("classe");
    const picker = this.openPicker("solo", (id) => {
      picker.ui.lock();
      this.setChosen(id, 1, true);
      audio.play(`classe-${id}`, { bus: "ui" });
      window.setTimeout(() => {
        if (this.picker !== picker) return;
        this.closePicker();
        void this.join({ ...choice, classId: id });
      }, SOLO_CONFIRM_MS);
    }, () => this.showMenu());
    const remembered = rememberedClass();
    picker.ui.update(remembered);
    picker.chosen = remembered;
    mount(this.ui, picker.ui.root);
    picker.ui.start();
  }

  /**
   * Abre a tela de escolha (`ClassSelect`) e o palco 3D por trás. O palco chega quando o pacote dos
   * avatares estiver pronto (quase sempre já está); a tela não espera por ele.
   */
  private openPicker(mode: "solo" | "lobby", onChoose: (id: ClassId) => void, onBack: () => void): NonNullable<AppFlow["picker"]> {
    this.closePicker();
    const ui = new ClassSelect({
      mode,
      onChoose,
      onBack,
      onHighlight: (id) => picker.stage?.focus(id),
      onLayout: (rects) => picker.stage?.layout(rects),
    });
    const picker: NonNullable<AppFlow["picker"]> = { mode, ui, stage: undefined, abort: new AbortController(), chosen: undefined, n: 1 };
    this.picker = picker;
    this.probe.classStage = () => picker.stage?.probe();
    // Fechada antes do palco chegar, a tela cancela: o palco atrasado não toca no laço da partida.
    startClassStage(this.renderer, picker.abort.signal)
      .then((stage) => {
        if (!stage) return;
        if (this.picker !== picker) return stage.stop();
        picker.stage = stage;
        // A escolha que já existia (a lembrada, a da sala) aparece sem o golpe.
        stage.choose(picker.chosen, picker.n, false);
        ui.replay();
      })
      .catch((error: unknown) => console.warn("palco da escolha da classe", error));
    return picker;
  }

  /**
   * Marca a minha classe na tela e no palco (F85). `explicit`: o jogador escolheu (ou já tinha escolhido
   * antes): lembra no navegador e acende o acento. A classe padrão de quem nunca escolheu só aparece marcada.
   */
  private setChosen(id: ClassId, n: number, explicit: boolean): void {
    const picker = this.picker;
    if (picker && (picker.chosen !== id || picker.n !== n)) {
      picker.chosen = id;
      picker.n = n;
      picker.stage?.choose(id, n);
    }
    if (explicit && (rememberedClass() !== id || document.body.dataset["tema"] !== "classe")) chooseClassTheme(id);
  }

  private closePicker(): void {
    const picker = this.picker;
    if (!picker) return;
    this.picker = undefined;
    picker.abort.abort();
    picker.ui.stop();
    picker.stage?.stop();
    delete this.probe.classStage;
  }

  private async join(choice: MenuChoice): Promise<void> {
    this.setScreen("conectando");
    mount(this.ui, h("section", { class: "panel" }, h("h2", {}, "Conectando…")));
    // O menu mostrava uma conta (ou convidado): se outra aba trocou ou a sessão venceu, confere antes.
    const current = this.account.session();
    if ((current?.id ?? "") !== choice.account) {
      this.showMenu(choice.account && !current ? "Sua sessão venceu. Entre de novo na conta ou jogue como convidado." : "A conta mudou em outra aba. Confira e tente de novo.");
      return;
    }
    const token = current?.token;
    this.entryAccount = choice.account;
    try {
      // Só em desenvolvimento: `?semente=` fixa o mundo do "Jogar sozinho" (o e2e da reentrada, Gate B1).
      const seed = import.meta.env.DEV && choice.action === "sozinho" ? new URLSearchParams(location.search).get("semente") : null;
      const options = { name: choice.name, mode: choice.mode, difficulty: choice.difficulty, classId: choice.classId, ...(token ? { token } : {}), ...(seed ? { seed } : {}) };
      const session =
        choice.action === "publica"
          ? await this.client.quickPlay(options)
          : choice.action === "privada"
            ? await this.client.createPrivate(options)
            : choice.action === "sozinho"
              ? await this.client.playSolo(options)
              : await this.client.joinByCode(choice.code ?? "", choice.name, token, choice.classId);
      this.enter(session);
    } catch (error) {
      console.error(error);
      this.showMenu(this.joinFailure(error, choice.action === "codigo" ? "Sala não encontrada, cheia ou já em andamento." : "Não foi possível conectar ao servidor."));
    }
  }

  /**
   * Motivo da recusa para o menu. A sessão recusada pelo servidor de jogo (vencida, de outro
   * segredo) sai, e o jogador escolhe entrar de novo ou seguir como convidado.
   */
  private joinFailure(error: unknown, fallback: string): string {
    const text = error instanceof Error ? error.message : "";
    if (text.includes(ENTRY_REFUSALS.sessao)) {
      this.account.logout();
      return "Sua sessão não vale mais. Entre de novo na conta ou jogue como convidado.";
    }
    if (text.includes(ENTRY_REFUSALS["conta-repetida"])) return "Essa conta já está nesta sala (em outra aba ou aparelho).";
    // Banida ou suspensa (F105): a sessão também caiu (banir e suspender derrubam as sessões); o jogo sai
    // da conta e mostra o motivo.
    if (text.includes(ENTRY_REFUSALS["conta-banida"]) || text.includes(ENTRY_REFUSALS["conta-suspensa"])) {
      this.account.logout();
      return text.includes(ENTRY_REFUSALS["conta-banida"]) ? ENTRY_REFUSALS["conta-banida"] : ENTRY_REFUSALS["conta-suspensa"];
    }
    // As outras recusas da porta da sala (pré-lançamento, sem o pré-acesso, tirada pela equipe, contas
    // fora do ar) já vêm com o texto para o jogador.
    const refusal = Object.values(ENTRY_REFUSALS).find((r) => text.includes(r));
    return refusal ?? fallback;
  }

  private enter(session: MatchSession): void {
    this.session = session;
    this.loadingStarted = false;
    this.reported = false;
    this.assetsReady = false;
    this.probe.roomCode = session.room.roomId;
    this.probe.sessionId = session.sessionId;
    session.onNotice((notice) => {
      toast(notice.message, notice.code === "mundo-divergente" ? "error" : "info");
      audio.play("ui-erro", { bus: "ui" });
    });
    session.onLeave((code) => this.left(code));
    // Revanche pronta: todos vão para a sala nova pelo código.
    session.room.onMessage(MSG.rematchReady, (e: RematchEvent) => void this.rematch(e.code));
    session.onState(() => this.sync());
    this.sync();
  }

  private sync(): void {
    const session = this.session;
    if (!session) return;
    const state = session.state;
    this.probe.phase = state.phase;
    this.probe.serverWorldHash = state.worldHash || undefined;
    switch (state.phase) {
      case "lobby": {
        this.setScreen("lobby");
        // A mesma escolha 3D do "Jogar sozinho" (F85): escolher troca a classe na sala; voltar leva ao "Sair".
        const fresh = this.picker?.mode !== "lobby";
        const choose = (id: ClassId) => {
          session.chooseClass(id);
          this.setChosen(id, (state.players.get(session.sessionId)?.slot ?? 0) + 1, true);
        };
        const picker = fresh ? this.openPicker("lobby", choose, () => this.ui.querySelector<HTMLElement>("#btn-sair-sala")?.focus()) : this.picker!;
        renderLobby(this.ui, session, () => void session.leave(), picker.ui);
        const me = state.players.get(session.sessionId);
        // A classe da sala é a escolhida se o jogador já escolheu alguma vez; senão é só a padrão.
        if (me) this.setChosen(classOf(me.classId).id, me.slot + 1, rememberedClass() !== undefined);
        if (fresh) picker.ui.start(this.ui);
        break;
      }
      case "carregando":
        this.closePicker();
        this.setScreen("carregando");
        if (!this.loadingStarted) this.startLoading(state.seed, state.mode as Mode);
        this.maybeReport();
        break;
      case "jogando":
        this.startPlaying();
        break;
      case "fim":
        this.finish(state.endReason);
        break;
    }
  }

  private startLoading(seed: number, mode: Mode): void {
    this.loadingStarted = true;
    this.loading = showLoading(this.ui, "Chegando a Eldrun…");
    const session = this.session;
    const abort = new AbortController();
    this.loadAbort = abort;
    loadWorld(seed, mode, (fraction, label) => this.loading?.set(fraction, label), abort.signal)
      .then((world) => {
        // Resultado de uma sessão que já acabou: descarta sem contaminar a atual.
        if (abort.signal.aborted || this.session !== session) {
          world.pool.terminate();
          return;
        }
        this.world = world;
        this.probe.worldHash = world.worldHash;
        this.probe.loadMs = world.loadMs;
        // Modelos, avatares (F49) e sons começaram a carregar ao abrir a página; quase sempre já
        // acabaram. Modelos e avatares nunca falham (sem eles, malhas e bonecos procedurais), mas a
        // cena precisa deles antes de nascer.
        this.loading?.set(1, "Montando o cenário…");
        return Promise.all([models.preload(), avatars.preload()])
          .then(() => {
            if (!abort.signal.aborted && this.session === session) {
              this.prewarmIcons();
              this.warmPost();
            }
            this.loading?.set(1, "Afinando os sons…");
            return audio.prerender();
          })
          .then(() => {
            if (abort.signal.aborted || this.session !== session) return;
            this.assetsReady = true;
            this.loading?.set(1, "Esperando os outros jogadores…");
            this.maybeReport();
          });
      })
      .catch((error: unknown) => {
        if (abort.signal.aborted) return;
        console.error(error);
        this.probe.error = "falha ao gerar a ilha";
        this.loading?.set(1, "Não foi possível gerar a ilha neste navegador.");
      });
  }

  /**
   * Ícones dos itens com modelo (F47) desenhados no carregamento, com os modelos já prontos: ~3 ms
   * cada no Chromium, ~0,1 s para os 42 (com a tocha da F48), escondidos atrás de "Montando o cenário…" em vez de um
   * engasgo ao abrir o inventário ou a fabricação. O cache vale para a página (e para a cena de jogo).
   * O cinto não dá para saber aqui: o servidor só cria o inventário ao começar a partida. Só enfeite:
   * com erro, os ícones são desenhados na hora, como antes, e o carregamento segue.
   */
  private prewarmIcons(): void {
    try {
      const icons = new ItemIcons(this.renderer);
      this.probe.prewarmedIcons = icons.prewarm(Object.keys(ITEM_LOOKS));
      icons.dispose();
    } catch (error) {
      console.warn("ícones dos itens não pré-aquecidos", error);
    }
  }

  /**
   * Compositor do pós-processamento (F52) do preset atual, criado e desenhado uma vez no carregamento
   * (sem tocar no canvas), atrás de "Montando o cenário…": os programas dele compilam aqui, não no
   * primeiro quadro da partida. Só enfeite: com erro, a cena o cria no começo.
   */
  private warmPost(): void {
    try {
      this.post.warm();
    } catch (error) {
      console.warn("pós-processamento não aquecido", error);
    }
  }

  /** Confirma ao servidor quando a ilha local e o hash do servidor estão prontos. */
  private maybeReport(): void {
    const state = this.session?.state;
    // Confirma só com a ilha, os modelos e os sons prontos (a cena nasce com tudo para tocar).
    if (this.reported || !this.world || !this.assetsReady || !state || state.worldHash === 0) return;
    this.reported = true;
    this.session?.reportLoaded(this.world.worldHash, this.world.resourceHash);
  }

  private startPlaying(): void {
    if (this.game || !this.world || !this.session) return;
    this.closePicker();
    this.setScreen("jogando");
    mount(this.ui);
    const game = new GameScene(this.renderer, this.world, this.session, this.post);
    game.start();
    this.game = game;
    this.probe.stats = () => game.stats();
    this.probe.avatars = () => game.probeAvatars();
    this.probe.creatures = () => game.probeCreatures();
    if (import.meta.env.DEV) {
      this.probe.primary = (held?: boolean) => game.primaryAction(held);
      this.probe.look = (yaw: number, pitch: number) => game.lookAt(yaw, pitch);
      this.probe.aimAt = (x: number, y: number, z: number) => game.aimAt(x, y, z);
      this.probe.cameraBench = (n: number) => game.cameraBench(n);
      this.probe.testPoi = (type: string) => game.testPoi(type);
      this.probe.selfDamage = (amount: number) => game.selfDamage(amount);
      this.probe.testBoss = (type: string, hp: number) => game.testBoss(type, hp);
      this.probe.testSaci = (options) => game.testSaci(options);
      this.probe.testRaft = (options) => game.testRaft(options);
      this.probe.testAmbience = (options) => game.testAmbience(options);
      this.probe.testCerco = (radius) => game.testCerco(radius);
      this.probe.testCreatures = (n) => game.testCreatures(n);
      this.probe.testSkills = (list) => game.testSkills(list);
      this.probe.testHits = (list) => game.testHits(list);
      this.probe.testStrike = (options) => game.testStrike(options);
      this.probe.testGrab = (delays) => game.testGrab(delays);
      this.probe.testHideFx = (groups) => game.testHideFx(groups);
      this.probe.testGoto = (x, z) => game.testGoto(x, z);
      this.probe.drawBreakdown = () => game.drawBreakdown();
      this.probe.grassBlades = (x, z, half, raw) => game.grassBlades(x, z, half, raw);
      this.probe.perf = (reset) => game.perf(reset);
    }
    this.probe.ready = true;
  }

  private finish(reason: string): void {
    if (this.probe.screen === "fim") return;
    this.closePicker();
    this.setScreen("fim");
    this.stopViewer();
    this.cancelLoading();
    const state = this.session?.state;
    // Trilha do fim: fanfarra na vitória (ou vencendo a Treta), tema lento no resto.
    const won = reason === "vitoria" || (reason === "treta" && !!state && state.winners.length === 1 && state.winners[0] === this.session?.sessionId);
    audio.setMusic(won ? "vitoria" : "derrota");
    const view = state
      ? endView({
          endReason: reason,
          winners: [...state.winners],
          mode: state.mode,
          difficulty: state.difficulty,
          seedCode: state.seedCode,
          matchSeconds: state.matchSeconds,
          day: state.clock.day,
          players: [...state.players.entries()],
          stats: state.stats,
        })
      : undefined;
    const repeat = h("input", { type: "checkbox", id: "repetir-seed" }) as HTMLInputElement;
    const rematch = h("button", { class: "primary", id: "revanche" }, "Revanche") as HTMLButtonElement;
    rematch.addEventListener("click", () => {
      rematch.disabled = true;
      rematch.textContent = "Abrindo a sala…";
      this.session?.room.send(MSG.rematch, { repeatSeed: repeat.checked });
    });
    mount(
      this.ui,
      h(
        "section",
        { class: "panel fim", "aria-label": "Fim de partida" },
        h("h2", {}, view?.title ?? "Fim de partida"),
        h("p", {}, endReasonText(reason, state, this.session?.sessionId) ?? "A partida terminou."),
        view ? h("dl", { class: "resumo" }, ...view.summary.flatMap(([k, v]) => [h("dt", {}, k), h("dd", {}, v)])) : null,
        view
          ? h(
              "div",
              { class: "tabela" },
              h(
                "table",
                { id: "estatisticas" },
                h("thead", {}, h("tr", {}, h("th", {}, "Jogador"), ...view.columns.map((c) => h("th", {}, c)))),
                h("tbody", {}, ...view.rows.map((r) => h("tr", {}, h("td", {}, r.name), ...r.values.map((v) => h("td", {}, String(v)))))),
              ),
            )
          : null,
        this.achievementsSection(reason),
        h("div", { class: "row" }, h("label", { class: "check" }, repeat, " repetir a seed"), rematch, h("button", { onclick: () => void this.session?.leave() }, "Voltar ao menu")),
      ),
    );
  }

  /**
   * Conquistas da partida (F34): quem jogou com conta vê as que ganhou, consultando a API pelo id
   * da partida (o servidor grava logo ao fim); convidado vê por que não pontuou. Abandono e falha de
   * carregamento não vão à API.
   */
  private achievementsSection(reason: string): HTMLElement | null {
    const state = this.session?.state;
    const me = state?.players.get(this.session?.sessionId ?? "");
    if (!state || !me || !["vitoria", "derrota", "treta"].includes(reason)) return null;
    // A conta que jogou (do servidor), mesmo que a sessão tenha vencido ou mudado depois.
    if (!me.userId) return h("p", { class: "hint", id: "conquistas" }, "Partida de convidado: não conta para rankings nem conquistas.");
    if (!state.matchId) return null;
    const status = h("p", {}, "Conferindo as conquistas…");
    const box = h("div", { class: "conquistas", id: "conquistas", "aria-live": "polite" }, h("h3", {}, "Conquistas"), status);
    this.achievementsAbort?.abort();
    const abort = new AbortController();
    this.achievementsAbort = abort;
    void this.account.achievements(state.matchId, me.userId, { signal: abort.signal }).then((result) => {
      if (abort.signal.aborted) return;
      if (result.kind === "indisponivel") {
        status.replaceChildren("Não deu para conferir agora. Veja no ", h("a", { href: `${this.siteUrl}/perfil`, target: "_blank", rel: "noopener" }, "seu perfil"), ".");
      } else if (result.names.length === 0) status.textContent = "Nenhuma conquista nova nesta partida.";
      else {
        status.textContent = result.names.length === 1 ? "Conquista desbloqueada:" : "Conquistas desbloqueadas:";
        box.append(h("ul", {}, ...result.names.map((n) => h("li", {}, n))));
        audio.play("tarefa", { bus: "ui" });
      }
    });
    return box;
  }

  /** Revanche: sai da sala que acabou e entra na nova com o mesmo nome (o saguão de novo). */
  private async rematch(code: string): Promise<void> {
    const session = this.session;
    if (!session || this.switching) return;
    const me = session.state.players.get(session.sessionId);
    const name = me?.name ?? "Jogador";
    // A revanche segue com a mesma classe (F85: a cor também).
    const classId = classOf(me?.classId ?? rememberedClass() ?? "").id;
    // A revanche segue com quem jogou: se outra aba trocou a conta ou a sessão venceu, volta ao
    // menu com o código (a escolha é do jogador).
    const current = this.account.session();
    if ((current?.id ?? "") !== this.entryAccount) {
      this.leaveNotice = `A conta mudou durante a partida. Para a revanche, confira a conta e entre com o código ${code}.`;
      await session.leave();
      return;
    }
    this.switching = true;
    this.achievementsAbort?.abort();
    await session.leave();
    try {
      // Com conta, a revanche leva o token (a sala nova também pontua).
      const next = await this.client.joinByCode(code, name, current?.token, classId);
      this.switching = false;
      this.enter(next);
    } catch (error) {
      console.error(error);
      this.switching = false;
      this.showMenu(this.joinFailure(error, "A sala da revanche não abriu."));
    }
  }

  private left(code: number): void {
    this.achievementsAbort?.abort();
    this.closePicker();
    this.stopViewer();
    this.cancelLoading();
    this.session = undefined;
    this.world?.pool.terminate();
    this.world = undefined;
    this.probe.ready = false;
    this.probe.worldHash = undefined;
    // Indo para a revanche: a sala nova abre o saguão.
    if (this.switching) return;
    const notice = this.leaveNotice;
    this.leaveNotice = undefined;
    this.showMenu(notice ?? (leaveMessage(code) || undefined));
  }

  private cancelLoading(): void {
    this.loadAbort?.abort();
    this.loadAbort = undefined;
    // O compositor aquecido para uma partida que não começou (saiu da sala, a carga falhou) sai junto.
    this.post.dispose();
  }

  private stopViewer(): void {
    this.game?.stop();
    this.game = undefined;
    delete this.probe.avatars;
    delete this.probe.creatures;
    delete this.probe.primary;
    delete this.probe.look;
    delete this.probe.aimAt;
    delete this.probe.cameraBench;
    delete this.probe.testPoi;
    delete this.probe.selfDamage;
    delete this.probe.testBoss;
    delete this.probe.testSaci;
    delete this.probe.testRaft;
    delete this.probe.testAmbience;
    delete this.probe.testCerco;
    delete this.probe.testCreatures;
    delete this.probe.testSkills;
    delete this.probe.testHits;
    delete this.probe.testStrike;
    delete this.probe.testGrab;
    delete this.probe.testHideFx;
    delete this.probe.testGoto;
    delete this.probe.drawBreakdown;
    delete this.probe.grassBlades;
    delete this.probe.perf;
  }

  private setScreen(screen: Screen): void {
    this.probe.screen = screen;
    document.body.dataset["screen"] = screen;
    // Menu com o tema; a escolha da classe e o lobby com os heróis (F84, F85); carregando em silêncio; em jogo, a cena manda.
    if (screen !== "jogando") audio.resetMix();
    if (screen === "menu" || screen === "conectando") audio.setMusic("menu");
    else if (screen === "lobby" || screen === "classe") audio.setMusic("lobby");
    else if (screen === "carregando") audio.setMusic("silencio");
  }
}
